import type {
  FavoriteGame,
  FavoriteGamesGetResponse,
  FavoriteGamesStorage,
} from '~/types/favorite-game';
import { FetchFavoriteGamesHandler } from '~/handlers/fetch-favorite-games.handler';
import { ToggleFavoriteGameHandler } from '~/handlers/toggle-favorite-game.handler';
import { ApostatudoApiFetchFavoriteGamesAdapter } from '~/adapters/api-fetch-favorite-games/apostatudo-api-fetch-favorite-games.adapter';
import { ApostatudoApiToggleFavoriteGameAdapter } from '~/adapters/api-toggle-favorite-game/apostatudo-api-toggle-favorite-game.adapter';

const STORAGE_KEY = 'jogos_favoritos';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

// Sistema de eventos para notificar componentes sobre mudanças
const favoriteGamesUpdateCallbacks = new Set<() => void>();

const notifyFavoriteGamesUpdate = () => {
  favoriteGamesUpdateCallbacks.forEach((callback) => callback());
};

export const useFavoriteGames = () => {
  const fetchHandler = new FetchFavoriteGamesHandler(
    new ApostatudoApiFetchFavoriteGamesAdapter(),
  );

  const toggleHandler = new ToggleFavoriteGameHandler(
    new ApostatudoApiToggleFavoriteGameAdapter(),
  );

  const getFromStorage = (): FavoriteGamesStorage | null => {
    if (typeof window === 'undefined') return null;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);

      if (!stored) return null;

      const data: FavoriteGamesStorage = JSON.parse(stored);

      // Verifica se o cache ainda é válido (TTL)
      if (data.updatedAt) {
        const cacheAge = Date.now() - new Date(data.updatedAt).getTime();

        if (cacheAge > CACHE_TTL) {
          console.log('Cache de favoritos expirado');
        }
      }

      return data;
    } catch {
      return null;
    }
  };

  const saveToStorage = (playerId: string, games: FavoriteGame[]): void => {
    if (typeof window === 'undefined') return;

    try {
      // Normaliza slugs para lowercase
      const normalizedGames = games.map((game) => ({
        ...game,
        slug: game.slug.toLowerCase(),
      }));

      const storageData: FavoriteGamesStorage = {
        player_code: playerId,
        payload: normalizedGames,
        updatedAt: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));

      // Notifica componentes sobre a atualização
      notifyFavoriteGamesUpdate();
    } catch {}
  };

  const clearStorage = (): void => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(STORAGE_KEY);

      // Notifica componentes sobre a limpeza
      notifyFavoriteGamesUpdate();
    } catch {}
  };

  const isCacheValid = (): boolean => {
    const data = getFromStorage();

    if (!data || !data.updatedAt) return false;

    const cacheAge = Date.now() - new Date(data.updatedAt).getTime();

    return cacheAge < CACHE_TTL;
  };

  const fetchAndStore = async (
    playerId: string,
    forceRefresh = false,
  ): Promise<FavoriteGamesGetResponse | null> => {
    // Se tem cache válido e não é refresh forçado, usa o cache
    if (!forceRefresh && isCacheValid()) {
      const cached = getFromStorage();

      if (cached) {
        return {
          player_code: cached.player_code,
          payload: cached.payload,
        };
      }
    }

    try {
      const data = await fetchHandler.handle(playerId);

      // null = falha na chamada (inclui o 401 do gate ENG-496): mantém o cache.
      if (!data) return null;

      if (data.payload && data.payload.length > 0) {
        saveToStorage(playerId, data.payload);

        return data;
      }

      // Resposta vazia de verdade: o jogador não tem favoritos.
      clearStorage();

      return null;
    } catch {
      return null;
    }
  };

  const toggleFavorite = async (
    playerId: string,
    gameId: number,
  ): Promise<boolean> => {
    // Salva estado anterior para possível rollback
    const currentGames = getFavoriteGames();
    const isFav = currentGames.some((game) => game.game_id === gameId);

    // Para remoção, podemos fazer optimistic update
    if (isFav) {
      // Remove temporariamente
      saveToStorage(
        playerId,
        currentGames.filter((game) => game.game_id !== gameId),
      );
    }

    try {
      // Só game_id — a identidade vem do Bearer (ENG-497); playerId fica como
      // chave do cache local.
      const result = await toggleHandler.handle({ game_id: gameId });

      if (result) {
        // Atualiza com dados reais da API (força refresh para pegar dados completos)
        await fetchAndStore(playerId, true);

        return result.is_favorited;
      }

      // Se falhou, reverte para o estado anterior
      console.warn('Falha ao alternar favorito - revertendo mudança');
      saveToStorage(playerId, currentGames);

      return false;
    } catch (error) {
      console.error('Erro ao alternar favorito:', error);

      // Reverte para o estado anterior em caso de erro
      saveToStorage(playerId, currentGames);

      return false;
    }
  };

  const getFavoriteGames = (): FavoriteGame[] => {
    const data = getFromStorage();

    return data?.payload || [];
  };

  const isFavorite = (gameId: number): boolean => {
    const games = getFavoriteGames();

    return games.some((game) => game.game_id === gameId);
  };

  const hasFavorites = (): boolean => {
    const games = getFavoriteGames();

    return games.length > 0;
  };

  const onFavoriteGamesUpdate = (callback: () => void): (() => void) => {
    favoriteGamesUpdateCallbacks.add(callback);

    // Retorna função para remover o listener
    return () => {
      favoriteGamesUpdateCallbacks.delete(callback);
    };
  };

  return {
    fetchAndStore,
    toggleFavorite,
    getFavoriteGames,
    isFavorite,
    hasFavorites,
    clearStorage,
    getFromStorage,
    onFavoriteGamesUpdate,
    isCacheValid,
  };
};
